import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { SEO_DEFAULTS } from '../shared/seo/seo.utils';

export interface SeoPageConfig {
  title: string;
  description: string;
  /** App path incl. leading slash, e.g. `/explorar`. */
  canonicalPath?: string;
  imageUrl?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private title   = inject(Title);
  private meta    = inject(Meta);
  private doc     = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  setPage(config: SeoPageConfig): void {
    const title = config.title.trim() || SEO_DEFAULTS.defaultTitle;
    const description = config.description.trim() || SEO_DEFAULTS.defaultDescription;
    const type = config.type ?? 'website';

    this.title.setTitle(title);
    this.setNamedMeta('description', description);
    this.setNamedMeta('robots', config.noIndex ? 'noindex, nofollow' : 'index, follow');

    this.setPropertyMeta('og:title', title);
    this.setPropertyMeta('og:description', description);
    this.setPropertyMeta('og:type', type);
    this.setPropertyMeta('og:site_name', SEO_DEFAULTS.siteName);

    this.setNamedMeta('twitter:card', config.imageUrl ? 'summary_large_image' : 'summary');
    this.setNamedMeta('twitter:title', title);
    this.setNamedMeta('twitter:description', description);

    const origin = this.siteOrigin();
    const canonicalPath = config.canonicalPath?.trim() || '/';
    const canonicalUrl = `${origin}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
    this.setCanonical(canonicalUrl);
    this.setPropertyMeta('og:url', canonicalUrl);

    if (config.imageUrl?.trim()) {
      const image = this.absoluteUrl(config.imageUrl.trim());
      this.setPropertyMeta('og:image', image);
      this.setNamedMeta('twitter:image', image);
    } else {
      this.removeMeta('property', 'og:image');
      this.removeMeta('name', 'twitter:image');
    }
  }

  resetToDefault(): void {
    this.removeJsonLd();
    this.setPage({
      title: SEO_DEFAULTS.defaultTitle,
      description: SEO_DEFAULTS.defaultDescription,
      canonicalPath: '/',
    });
  }

  /** Absolute URL for assets and structured data (works on browser and prerender). */
  toAbsoluteUrl(url: string): string {
    return this.absoluteUrl(url);
  }

  /** Injects or replaces a JSON-LD `<script type="application/ld+json">` in `<head>`. */
  setJsonLd(key: string, payload: unknown): void {
    const id = this.jsonLdScriptId(key);
    this.doc.getElementById(id)?.remove();

    const script = this.doc.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.setAttribute('data-tfr-seo', 'json-ld');
    script.setAttribute('data-tfr-jsonld-key', key);
    script.textContent = JSON.stringify(payload);
    this.doc.head.appendChild(script);
  }

  /** Removes JSON-LD scripts injected by this service. */
  removeJsonLd(key?: string): void {
    if (key) {
      this.doc.getElementById(this.jsonLdScriptId(key))?.remove();
      return;
    }
    this.doc.head
      .querySelectorAll('script[data-tfr-seo="json-ld"]')
      .forEach(node => node.remove());
  }

  siteOrigin(): string {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      return window.location.origin.replace(/\/$/, '');
    }
    return environment.publicOrigin.replace(/\/$/, '');
  }

  private absoluteUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    const origin = this.siteOrigin();
    return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
  }

  private setCanonical(href: string): void {
    const head = this.doc.head;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private setNamedMeta(name: string, content: string): void {
    if (this.meta.getTag(`name='${name}'`)) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.addTag({ name, content });
    }
  }

  private setPropertyMeta(property: string, content: string): void {
    if (this.meta.getTag(`property='${property}'`)) {
      this.meta.updateTag({ property, content });
    } else {
      this.meta.addTag({ property, content });
    }
  }

  private removeMeta(attr: 'name' | 'property', key: string): void {
    this.meta.removeTag(`${attr}='${key}'`);
  }

  private jsonLdScriptId(key: string): string {
    return `tfr-jsonld-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }
}
